import { format, parseISO } from "date-fns";

import type { ReactNode } from "react";
import { FiMinus, FiTrendingDown, FiTrendingUp } from "react-icons/fi";

import type {
  FieldOption,
  MeasurementField,
  MeasurementValueSummary,
  SeverityLevel,
  TrendDirection,
} from "@/types/measurement";

import {
  formatHeightDisplay,
  isHeightField,
  roundToQuarterInch,
} from "./measurement-utils";

/**
 * Look up a stored raw value in a field's option list and return its label.
 * Falls back to the stringified raw value when the field has no options or
 * the value isn't recognised (so callers don't have to special-case).
 */
export function getOptionLabel(
  field: Pick<MeasurementField, "options"> | { options?: FieldOption[] | null },
  rawValue: string | number | null | undefined,
): string {
  if (rawValue == null) return "";
  const options = field.options;
  if (!options?.length) return String(rawValue);
  const match = options.find((o) => String(o.value) === String(rawValue));
  if (!match) return String(rawValue);
  const label = match.label?.trim();
  return label ? label : String(match.value);
}

/**
 * Format a measurement value for display based on field config
 */
export function formatMeasurementValue(
  value: MeasurementValueSummary,
  field: MeasurementField,
): string {
  if (value.arrayValue && value.arrayValue.length > 0) {
    return value.arrayValue.map((v) => getOptionLabel(field, v)).join(", ");
  }

  if (value.textValue != null) {
    if (field.dataType === "DATE") {
      // Stored as an ISO calendar date (YYYY-MM-DD). Format from the date's
      // local components (no timezone shift) so the displayed day matches what
      // was entered regardless of the viewer's timezone.
      try {
        return format(parseISO(value.textValue), "MMM d, yyyy");
      } catch {
        return value.textValue;
      }
    }
    return getOptionLabel(field, value.textValue);
  }

  if (value.numericValue == null) {
    return "—";
  }

  if (field.options?.length) {
    return getOptionLabel(field, value.numericValue);
  }

  if (isHeightField(field)) {
    return formatHeightDisplay(value.numericValue);
  }

  const decimalPlaces = field.decimalPlaces ?? 1;
  const formatted = value.numericValue.toFixed(decimalPlaces);
  return field.unit ? `${formatted} ${field.unit}` : formatted;
}

/**
 * Map severity to theme-aware CSS class
 */
export function getSeverityColor(severity: SeverityLevel): string {
  switch (severity) {
    case "NORMAL":
      return "text-status-success-text";
    case "WARNING":
      return "text-status-warning-text";
    case "HIGH":
    case "CRITICAL":
      return "text-status-error-text";
    default:
      return "text-foreground-secondary";
  }
}

/**
 * Map severity to background CSS class
 */
export function getSeverityBgColor(severity: SeverityLevel): string {
  switch (severity) {
    case "NORMAL":
      return "bg-status-success-bg";
    case "WARNING":
      return "bg-status-warning-bg";
    case "HIGH":
    case "CRITICAL":
      return "bg-status-error-bg";
    default:
      return "bg-background-secondary";
  }
}

/**
 * Severity as a large SVG fill (the Body diagram's segments). Deliberately not
 * the status *text* colors: those are tuned to be read as text, so on a light
 * theme they are near-black browns and greens that turn each limb into a muddy
 * slab. The segment tokens carry the same meanings as a pastel in both themes.
 */
export function getSeverityFillClass(severity: SeverityLevel | null): string {
  switch (severity) {
    case "NORMAL":
      return "fill-measurement-segment-normal";
    case "WARNING":
      return "fill-measurement-segment-warning";
    case "HIGH":
    case "CRITICAL":
      return "fill-measurement-segment-high";
    default:
      return "fill-background-tertiary";
  }
}

/** The same segment tokens as a bar fill (the Body card's range bars). */
export function getSeverityBarClass(severity: SeverityLevel | null): string {
  switch (severity) {
    case "NORMAL":
      return "bg-measurement-segment-normal";
    case "WARNING":
      return "bg-measurement-segment-warning";
    case "HIGH":
    case "CRITICAL":
      return "bg-measurement-segment-high";
    default:
      return "bg-foreground-muted/40";
  }
}

/**
 * Get trend icon for a pre-computed trend direction
 */
export function getTrendIcon(direction: TrendDirection | null): ReactNode {
  switch (direction) {
    case "up-good":
    case "up-bad":
      return <FiTrendingUp size={14} />;
    case "down-good":
    case "down-bad":
      return <FiTrendingDown size={14} />;
    case "neutral":
      return <FiMinus size={14} />;
    default:
      return null;
  }
}

/**
 * Get color class for a trend direction
 */
export function getTrendColor(direction: TrendDirection | null): string {
  switch (direction) {
    case "up-good":
    case "down-good":
      return "text-status-success-text";
    case "up-bad":
    case "down-bad":
      return "text-status-error-text";
    case "neutral":
      return "text-foreground-secondary";
    default:
      return "";
  }
}

/**
 * Generate a stable HSL color from a string (e.g. field ID).
 * Uses a simple hash to pick a hue so the same ID always produces the same color.
 */
export function hashToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = ((hash % 360) + 360) % 360;
  return `hsl(${hue}, 65%, 50%)`;
}

/**
 * Get the chart color for a field. Checks field-level chartPanels fieldOverrides
 * first, then falls back to hashToColor.
 */
export function getFieldChartColor(field: MeasurementField): string {
  const panels = field.displayConfig?.chartPanels;
  if (panels?.length) {
    const override = panels[0].fieldOverrides?.[field.id];
    if (override?.color) return override.color;
  }
  return hashToColor(field.id);
}

/**
 * Compute a trend direction from a numeric change and polarity preference.
 * Mirrors the backend's `get_trend_direction` logic.
 */
export function computeTrendDirection(
  change: number | null,
  higherIsBetter?: boolean | null,
): TrendDirection | null {
  if (change == null) return null;
  if (change === 0) return "neutral";
  const goingUp = change > 0;
  if (higherIsBetter == null) return goingUp ? "up-good" : "down-bad";
  if (goingUp) return higherIsBetter ? "up-good" : "up-bad";
  return higherIsBetter ? "down-bad" : "down-good";
}

/**
 * Format a numeric change value with sign
 */
export function formatChange(
  change: number | null,
  field: MeasurementField,
): string {
  if (change == null) return "—";
  if (change === 0) return "No change";

  if (isHeightField(field)) {
    const rounded = roundToQuarterInch(change);
    if (rounded === 0) return "No change";
    const sign = rounded > 0 ? "+" : "";
    return `${sign}${rounded} in`;
  }

  const decimalPlaces = field.decimalPlaces ?? 1;
  const sign = change > 0 ? "+" : "";
  const formatted = change.toFixed(decimalPlaces);
  return field.unit
    ? `${sign}${formatted} ${field.unit}`
    : `${sign}${formatted}`;
}
